import {useAuth} from '@apps-common/auth-react';
import {
  GpsOff as PartialRectIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import {Box, Card, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {useDrag} from 'react-dnd';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import MetadataModal from '../metadata-modal/index.jsx';
import {focusItems, unfocusItems} from '@explorer/actions/focused-item';
import {notify} from '@explorer/actions/notifications';
import {
  quickFocusItems,
  quickUnfocusAllItems,
} from '@explorer/actions/quick-focused-item';
import {
  addItemsToOrder,
  addItemsToView,
  removeItemsFromOrder,
  removeItemsFromView,
} from '@explorer/actions/results';
import {trackEvent} from '@explorer/analytics';
import {
  EyeBadgeIcon,
  FootprintToggleButton,
  OrderToggleButton,
  ShareToggleButton,
} from '@explorer/components/items-panel/item-components';
import {
  CARD_LAYOUT_STYLES,
  THUMBNAIL_DIMENSIONS_V2,
} from '@explorer/components/items-panel/styles';
import CopyButton from '@explorer/components/ui/copy-button';
import ItemType from '@explorer/components/ui/item-type';
import {
  CloudCoverIndicator,
  IndicatorsContainer,
  ItemIndicator,
} from '@explorer/components/ui/metadata-indicators';
import ThumbnailCard from '@explorer/components/ui/thumbnail-card';
import ThumbnailImage from '@explorer/components/ui/thumbnail-image';
import {COMPARE_TYPES, LABS_MAP, UI_MODES} from '@explorer/constants';
import {getUniqueId} from '@explorer/util/items';
import {
  hasPartialRectification,
  isCompareSubject,
} from '@explorer/util/results';
import {getUrl as getThumbUrl} from '@explorer/util/thumbs';

const SceneListItem = ({
  resultId,
  sceneItem,
  itemIndex,
  groupIndex,
  canUserDownload,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const auth = useAuth();

  const [showMetadata, setShowMetadata] = useState(false);

  const uniqueId = getUniqueId(sceneItem);

  const isComparing = useSelector(state => state.ui.mode === UI_MODES.COMPARE);
  const isComparingSubject = useSelector(
    state =>
      isCompareSubject(state.compare.subjects, sceneItem.id) ||
      isCompareSubject(state.compare.subjects, resultId)
  );
  const isViewSelected = useSelector(state =>
    state.results.viewItems.includes(uniqueId)
  );
  const isOrderSelected = useSelector(state =>
    state.results.orderItems.includes(uniqueId)
  );
  const showSharingMenu = useSelector(
    state => state.labs[LABS_MAP.RESULT_SCENE_SHARE_MENU]
  );
  const isFootprintLocked = useSelector(state =>
    state.focusedItems?.includes(sceneItem)
  );

  const [{isDragging}, dragRef] = useDrag(
    () => ({
      type: COMPARE_TYPES.SCENE,
      item: {
        resultId: resultId,
        ...sceneItem,
        properties: {
          ...sceneItem.properties,
          items: [sceneItem],
        },
      },
      canDrag: isComparing,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [isComparing]
  );

  const onOrderButtonClick = () => {
    const payload = {
      resultId,
      items: [uniqueId],
    };

    if (isOrderSelected) {
      dispatch(removeItemsFromOrder(payload));
      trackEvent('Scene List: Remove scene from cart', {
        itemType: sceneItem.properties.itemType,
        groupNumber: groupIndex + 1,
        itemNumber: itemIndex + 1,
      });
    } else {
      dispatch(addItemsToOrder(payload));
      trackEvent('Scene List: Add scene to cart', {
        itemType: sceneItem.properties.itemType,
        groupNumber: groupIndex + 1,
        itemNumber: itemIndex + 1,
      });
    }
  };

  const onViewButtonClick = () => {
    const payload = {
      resultId,
      items: [uniqueId],
    };

    if (isViewSelected) {
      dispatch(removeItemsFromView(payload));
      trackEvent('Scene List: Hide scene on map', {
        itemType: sceneItem.properties.itemType,
        groupNumber: groupIndex + 1,
        itemNumber: itemIndex + 1,
      });
    } else {
      dispatch(addItemsToView(payload));
      trackEvent('Scene List: View scene on map', {
        itemType: sceneItem.properties.itemType,
        groupNumber: groupIndex + 1,
        itemNumber: itemIndex + 1,
      });
    }
  };

  const toggleShowMetadata = () => {
    setShowMetadata(!showMetadata);
    trackEvent(
      showMetadata ? 'Scene List: Hide metadata' : 'Scene List: Show metadata'
    );
  };

  const onShareClick = () => {
    const baseURL = window.location.href.split('#')[0];
    const shareUrl = `${baseURL}?item=${sceneItem.properties.item_type}::${sceneItem.id}`;

    copy(shareUrl);

    dispatch(
      notify({
        message: t('sceneListItem.notifyCopied'),
      })
    );
  };

  const toggleFootprint = () => {
    if (isFootprintLocked) {
      dispatch(unfocusItems({items: [sceneItem]}));
      trackEvent('Scene List: Hide item footprint', {
        itemType: sceneItem.properties.itemType,
        groupNumber: groupIndex + 1,
        itemNumber: itemIndex + 1,
      });
    } else {
      dispatch(focusItems({items: [sceneItem]}));
      trackEvent('Scene List: Show item footprint', {
        itemType: sceneItem.properties.itemType,
        groupNumber: groupIndex + 1,
        itemNumber: itemIndex + 1,
      });
    }
  };

  const quickShowFootprint = () => {
    dispatch(quickFocusItems({items: [sceneItem]}));
  };

  const quickHideFootprint = () => {
    dispatch(quickUnfocusAllItems());
  };

  const handleSceneIDCopy = () => {
    trackEvent('Copied scene id');
  };

  const canOrder = canUserDownload && sceneItem.properties.canDownload;
  const cloudCover =
    sceneItem.properties.cloud_cover >= 0
      ? Math.round(sceneItem.properties.cloud_cover * 100)
      : null;

  return (
    <React.Fragment>
      <Card
        ref={dragRef}
        sx={{
          cursor: !isComparing ? 'default' : isDragging ? 'grabbing' : 'grab',
          transform: 'translate3d(0, 0, 0)',
          backgroundColor:
            isComparingSubject && isComparing
              ? 'primary.lighter'
              : 'background.paper',
          ...CARD_LAYOUT_STYLES,
          boxSizing: 'border-box',
          flexGrow: 1,
          padding: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        elevation={0}
        square
        draggable={isComparing}
      >
        <ThumbnailCard
          button={!isComparing}
          selected={!isComparing && isViewSelected}
          aria-label={
            isViewSelected
              ? t('sceneListItem.removeFromMap')
              : t('sceneListItem.addToMap')
          }
          onClick={onViewButtonClick}
          thumbnail={
            <ThumbnailImage
              altText={t('sceneListItem.thumbnailLabel', {itemId: uniqueId})}
              src={getThumbUrl({
                item: sceneItem,
                token: auth.user?.accessToken,
              })}
              sx={{
                width: THUMBNAIL_DIMENSIONS_V2,
                height: THUMBNAIL_DIMENSIONS_V2,
              }}
            />
          }
          sx={{
            marginRight: 1,
            width: THUMBNAIL_DIMENSIONS_V2,
            gridArea: 'thumb',
          }}
          data-qe="view-on-map-button"
        >
          {!isComparing && <EyeBadgeIcon isViewSelected={isViewSelected} />}
        </ThumbnailCard>
        <Box
          sx={{
            gridArea: 'title',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              display: 'inline',
              mr: 1,
            }}
          >
            {t('common:time.long', {
              date: sceneItem.properties.acquired,
            })}
          </Typography>
          <Tooltip title={t('sceneListItem.viewDetails')}>
            <IconButton
              onClick={toggleShowMetadata}
              value="viewItemMetadata"
              size="small"
              sx={{
                borderRadius: 1,
                padding: 0.25,
              }}
            >
              <InfoOutlinedIcon sx={{fontSize: 18}} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            gridArea: 'content',
          }}
        >
          <ItemType
            showIcon={false}
            itemType={sceneItem.properties.item_type}
            typographyProps={{
              variant: 'body2',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{fontFamily: 'monospace', marginRight: 1}}
              data-qe="scene-id"
              noWrap
            >
              {sceneItem.id}
            </Typography>
            <CopyButton
              onCopy={handleSceneIDCopy}
              textToCopy={sceneItem.id}
              title="Scene ID"
              size="small"
              buttonStyleOverrides={{
                padding: 0.5,
              }}
            />
          </Box>

          <IndicatorsContainer>
            {cloudCover !== null && (
              <CloudCoverIndicator
                cloudPercent={cloudCover}
                data-qe="item-cloud-cover"
              />
            )}
            {hasPartialRectification([sceneItem]) && (
              <ItemIndicator
                tooltip={t('sceneListItem.partiallyRectified')}
                icon={<PartialRectIcon />}
              />
            )}
          </IndicatorsContainer>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            gridArea: 'action',
          }}
        >
          {showSharingMenu && (
            <Tooltip title={t('sceneListItem.copyLink')}>
              <ShareToggleButton
                onClick={onShareClick}
                value="shareItemLink"
                data-qe="image-copy-link"
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              canOrder
                ? t(
                    `sceneListItem.${
                      isOrderSelected ? 'removeFromOrder' : 'addToOrder'
                    }`
                  )
                : t('sceneListItem.orderNoPermissions')
            }
          >
            <OrderToggleButton
              disabled={!canOrder}
              selected={isOrderSelected}
              onClick={onOrderButtonClick}
              value="orderScene"
            />
          </Tooltip>
          <Tooltip
            title={
              isFootprintLocked
                ? t('sceneListItem.removeFootprintFromMap')
                : t('sceneListItem.addFootprintToMap')
            }
          >
            <FootprintToggleButton
              selected={isFootprintLocked}
              value="viewItemFootprint"
              onClick={toggleFootprint}
              onMouseEnter={quickShowFootprint}
              onFocus={quickShowFootprint}
              onMouseLeave={quickHideFootprint}
              onBlur={quickHideFootprint}
            />
          </Tooltip>
        </Stack>
      </Card>
      <MetadataModal
        open={showMetadata}
        onClose={() => {
          setShowMetadata(false);
          trackEvent('Scene List: Hide metadata');
        }}
        item={sceneItem}
      />
    </React.Fragment>
  );
};

SceneListItem.propTypes = {
  resultId: PropTypes.string.isRequired,
  sceneItem: PropTypes.object.isRequired,
  itemIndex: PropTypes.number.isRequired,
  groupIndex: PropTypes.number.isRequired,
  canUserDownload: PropTypes.bool,
};

export default SceneListItem;
